<template>
  <section v-if="Boolean(products.length)">
    <div class="product-list" ref="productListRef">
      <h4 v-if="heading" id="product-list-heading" class="product-list__heading">{{ heading }}</h4>

      <!-- Swipable products for mobile devices -->
      <ProductSwiper
        v-if="enableSwiper"
        :products="visibleProducts"
        :enableReviews="false"
        :enableViewProductDetailsCTA="true"
        :layer="layer"
        :subcategory="subcategory"
        :selectedProducts="selectedProductsFromContext"
        :showProductCardInfo="showProductCardInfo"
        @product-click="handleProductSelected"
        @view-product-details="onViewProductDetailsClick"
      />

      <!-- Desktop view -->
      <div v-else class="product-list__product-cards" role="list" aria-labelledby="product-list-heading">
        <div v-for="(product, index) in visibleProducts" :key="index" class="product-list__product-card" ref="productCards">
          <ProductCard
            type="TypeE"
            :product="product"
            :enableReviews="false"
            :layer="layer"
            :subcategory="subcategory"
            :selectedProducts="selectedProductsFromContext"
            @product-click="handleProductSelected"
            @view-product-details="onViewProductDetailsClick"
            :showWishlistIcon="false"
          />
        </div>
      </div>

      <!-- Show more button -->
      <div v-if="!enableSwiper" class="product-list__bottom-actions">
        <button
          v-if="products.length > initialVisibleCount"
          class="show-more"
          @click="toggleView"
          :aria-expanded="showMore.toString()"
        >
          {{ buttonText }}
        </button>
      </div>
    </div>
  </section>
</template>

<script setup>
  import { ref, toRefs, computed, reactive, nextTick, onMounted } from 'vue';
  import { useStore } from 'vuex';
  import ProductCard from '../../global/product-card/index.vue';
  import ProductSwiper from '../components/product-swiper.vue';

  const props = defineProps({
    heading: String,
    products: {
      type: Array,
      default: () => [],
    },
    expandText: {
      type: String,
      default: 'View More',
    },
    collapseText: {
      type: String,
      default: 'View Less',
    },
    initialVisibleCount: {
      type: Number,
      default: 4,
    },
    layer: String,
    subcategory: String,
    enableSwiper: {
      type: Boolean,
      default: false,
    },
    showProductCardInfo: {
      type: Boolean,
      default: true,
    },
    onViewProductDetailsClick: {
      type: Function,
    },
  });

  const {
    heading,
    products,
    expandText,
    collapseText,
    initialVisibleCount,
    layer,
    subcategory,
    enableSwiper,
    showProductCardInfo,
  } = toRefs(props);

  const store = useStore();
  const selectedProductsFromContext = reactive(store.state.byk.selectedProducts);

  const visibleCount = ref(initialVisibleCount.value);
  const showMore = ref(false);
  const productListRef = ref(null);
  const productCards = ref([]);

  onMounted(() => {
    expandSectionOnRender();
  });

  const toggleView = () => {
    if (showMore.value) {
      // less clicked
      visibleCount.value = props.initialVisibleCount;
      // Scroll to the product list when View Less is clicked
      /**
       * nextTick is added to fix safari browser issue to scroll up, as in different browser scroll sometimes work differently
       * and it is taking scroll height before dynamic data change so, we need some delay once data changed then try to scroll.
       */
      nextTick(() => {
        productListRef.value.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    } else {
      // more clicked
      visibleCount.value = props.products.length;
      nextTick(() => {
        // Focus on the first product card after expanding
        const firstProductCard = productCards.value[props.initialVisibleCount];
        
        if (firstProductCard) {
          const firstFocusableElement = firstProductCard.querySelector('a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])');
          if (firstFocusableElement) {
            firstFocusableElement.focus(); // Focus on the first focusable element
          }
        }
      });
    }
    showMore.value = !showMore.value;
  };

  const buttonText = computed(() => (showMore.value ? collapseText.value : expandText.value));

  const visibleProducts = computed(() => {
    return enableSwiper.value ? products.value : products.value.slice(0, visibleCount.value);
  });

  const handleProductSelected = (selection = {}) => {
    store.dispatch('updateBYKSelectedProduct', {
      layer: selection.layer,
      subcategory: selection.subcategory,
      value: selection.product ? [selection.product] : [],
    });
  };

  const expandSectionOnRender = () => {
    // No need to implement in swiper case
    if (enableSwiper.value) {
      return;
    }

    const selectedProduct = selectedProductsFromContext[layer.value]?.[subcategory.value]?.[0];
    if (!selectedProduct) return;

    const isProductVisible = visibleProducts.value?.some((product) => product.id === selectedProduct.id);

    showMore.value = !isProductVisible;
    visibleCount.value = isProductVisible ? initialVisibleCount.value : products.value.length;
  };
</script>
